import { useMemo } from 'react';

import getDeviceType from '@vimeo/ui/core/utils/getDeviceType';
import {
  buildActionBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContextFromTeamUser,
  buildThirdPartyIntegrationBpContext,
  buildVideoBpContext,
  buildWebBpContext,
  PartialWithRequired,
  ProductAnalyticsBpContext,
  sendBpEventWithContexts,
} from '@vimeo/use-tracking';

import { useGlobalStore } from '../state/globalStore';
import { BP_EVENT_NAMES } from '../utils/bpUtils/constants';
import { buildLiveBpContext } from '../utils/bpUtils/LiveBpContextWithVersion';
import { buildViewBpContext } from '../utils/bpUtils/ViewBpContextWithVersion';
import {
  CustomFieldTypes,
  ENTITY_TYPE,
  PROVIDER_ID_TO_SERVICE_ID_MAPPING,
} from '../utils/constants';
import { getFormattedEntityType, isWindowParentSameOrigin } from '../utils/helper';
import { Enum } from '../utils/types';

interface SendRegistrationActionBP {
  eventName: Enum<
    Pick<typeof BP_EVENT_NAMES, 'REGISTRATION_FORM_VIEWED' | 'REGISTRATION_FORM_COMPLETE'>
  >;
}

export const useViewerAnalytics = () => {
  const entityType = useGlobalStore((store) => store.entityType);
  const entityId = useGlobalStore((store) => store.entityId);
  const entityOwnerId = useGlobalStore((store) => store.entityOwnerId) || null;
  const videoDuration = useGlobalStore((store) => store.entity.duration) || null;
  const uuid = useGlobalStore((state) => state.leadCapture.uuid);
  const parentEntityType = useGlobalStore((state) => state.parentEntityType) || null;

  const resourceViewPrivacy = useGlobalStore((store) => store.entity?.privacy?.view);
  const resourceEmbedPrivacy = useGlobalStore((store) => store.entity?.privacy?.embed);
  const hasPrivacyPolicyUrl = useGlobalStore((store) =>
    Boolean(store.leadCapture.privacyPolicyUrl),
  );
  const providerListData = useGlobalStore((store) => store.leadCapture.emailLists || []);

  const customFields = useGlobalStore((store) => store.leadCapture.customFields || []);
  const hiddenFields = useGlobalStore((store) => store.leadCapture.hiddenFields || []);
  const isLogoActive = useGlobalStore((store) => store.leadCapture.logo?.isActive);
  const isBackgroundActive = useGlobalStore((store) => store.leadCapture.background?.isActive);
  const layout = useGlobalStore((store) => store.leadCapture.layout);
  const isSkippable = useGlobalStore((store) => store.leadCapture.isSkippable) ?? false;

  const { dropdownFieldsCount, textFieldsCount } = useMemo(() => {
    const dropdownFieldsCount =
      customFields.filter((field) => field.type === CustomFieldTypes.Dropdown).length || 0;

    const textFieldsCount =
      customFields.filter((field) => field.type === CustomFieldTypes.Text).length || 0;

    return { dropdownFieldsCount, textFieldsCount };
  }, [customFields]);

  const hiddenFieldsCount = hiddenFields.length;

  const placement = useGlobalStore((store) => store.leadCapture.placement);

  const eventPrivacy = useMemo(() => {
    if (entityType === ENTITY_TYPE.EVENT) {
      switch (resourceViewPrivacy) {
        case 'anybody':
          return 'public';
        case 'nobody':
          return 'private';
        case 'password':
          return 'password';
        case 'unlisted':
          return 'unlisted';
        case 'embed_only':
          return 'hide_from_vimeo';
      }
    }
    return null;
  }, [resourceViewPrivacy, entityType]);

  const providerNames = useMemo(() => {
    return Array.from(
      new Set(
        providerListData
          .map((p) =>
            p.providerId ? PROVIDER_ID_TO_SERVICE_ID_MAPPING[parseInt(p.providerId)] : null,
          )
          .filter((p): p is string => Boolean(p)),
      ),
    );
  }, [providerListData]);

  const prefillStatus = useMemo(() => {
    if (customFields.every((field) => field.default)) {
      return 'fully';
    }
    if (customFields.some((field) => field.default)) {
      return 'partially';
    }
    return null;
  }, [customFields]);

  const prefilledFrom = useMemo(() => {
    if (parentEntityType === ENTITY_TYPE.SHOWCASE) {
      return 'showcase';
    }
    return parentEntityType;
  }, [parentEntityType]);

  const isIntegration = providerNames.length > 0;

  const viewLocation = isWindowParentSameOrigin() ? 'local' : 'embed';

  /** context builders */

  const buildActionContext = () => buildActionBpContext({ action_type: 'click', feature: null });

  const buildViewContext = () =>
    buildViewBpContext({ view_type: 'impression', feature: null, is_empty_state: null }, 7);

  const buildWebContext = () =>
    buildWebBpContext({
      path: window.location.href,
      page_name: viewLocation === 'local' ? 'viewer_home' : 'embedded_page',
    });

  const buildProductAnalyticsContext = ({
    element,
    copy,
  }: PartialWithRequired<ProductAnalyticsBpContext, 'copy' | 'element'>) =>
    buildProductAnalyticsBpContext({
      product: 'gates',
      feature: 'registration',
      location: 'player',
      element,
      copy,
      device_type: getDeviceType(),
      entity_type: getFormattedEntityType(entityType),
      scrolling_percentage: null,
    });

  const buildVideoContext = () =>
    buildVideoBpContext({
      video_id: Number(entityId),
      video_privacy: resourceViewPrivacy,
      video_embed_privacy: resourceEmbedPrivacy,
      duration: videoDuration,
    });

  const buildLiveContext = () =>
    buildLiveBpContext(
      {
        recurring_live_event_id: entityId,
        event_privacy: eventPrivacy,
        live_feature: 'registration',
        audience_type: 'live_event',
      },
      6,
    );

  const buildThirdPartyIntegrationContext = () =>
    buildThirdPartyIntegrationBpContext({
      is_integration: isIntegration,
      integration_id: null,
      integration_name: null,
      managed_user_id: null,
      app_id: null,
      integration_type: null,
      partner_bucket: null,
      is_partner: null,
    });

  const buildTeamContext = () => buildTeamBpContextFromTeamUser(undefined);

  /** Event specific fields */

  const buildAdditionalFields = () => ({
    form_id: uuid,
    provider_name: providerNames,
    dropdown_fields_count: dropdownFieldsCount,
    text_fields_count: textFieldsCount,
    hidden_fields_count: hiddenFieldsCount,
    layout: layout,
    is_logo_added: isLogoActive,
    is_background_added: isBackgroundActive,
    is_skippable: isSkippable,
    placement: entityType === ENTITY_TYPE.VIDEO ? placement : null,
    is_privacy_policy_url: hasPrivacyPolicyUrl,
    view_location: viewLocation,
    prefilled_data: prefillStatus,
    prefilled_from: prefilledFrom,
    owner_id: entityOwnerId != null ? String(entityOwnerId) : null,
  });

  /** BP event handlers */

  const sendRegistrationFormCompleteEvent = () => {
    const eventName =
      BP_EVENT_NAMES.REGISTRATION_FORM_COMPLETE as 'vimeo.registration_form_complete';
    const contexts = {
      ...buildActionContext(),
      ...buildWebContext(),
      ...buildProductAnalyticsContext({
        element: 'button',
        copy: 'register',
      }),
      ...(entityType === ENTITY_TYPE.VIDEO && buildVideoContext()),
      ...(entityType === ENTITY_TYPE.EVENT && buildLiveContext()),
      ...buildThirdPartyIntegrationContext(),
      ...buildTeamContext(),
    };
    sendBpEventWithContexts(eventName, contexts, 6, {
      ...buildAdditionalFields(),
    });
  };

  const sendRegistrationFormViewedEvent = () => {
    const eventName = BP_EVENT_NAMES.REGISTRATION_FORM_VIEWED as 'vimeo.registration_form_viewed';
    const contexts = {
      ...buildViewContext(),
      ...buildWebContext(),
      ...buildProductAnalyticsContext({
        element: null,
        copy: null,
      }),
      ...(entityType === ENTITY_TYPE.VIDEO && buildVideoContext()),
      ...(entityType === ENTITY_TYPE.EVENT && buildLiveContext()),
      ...buildThirdPartyIntegrationContext(),
      ...buildTeamContext(),
    };
    sendBpEventWithContexts(eventName, contexts, 4, {
      ...buildAdditionalFields(),
    });
  };

  return {
    sendRegistrationFormCompleteEvent,
    sendRegistrationFormViewedEvent,
  };
};
